// @ts-nocheck
import React from 'react';
import './FeatureIntro.css';

interface FeatureIntroProps {
  onStart: () => void;
  onBack: () => void;
}

interface ChangelogEntry {
  version: string;
  date: string;
  changes: Array<{ type: string; text: string }>;
}

// 版本更新记录配置
const CHANGELOG: ChangelogEntry[] = [
  {
    version: 'v2.3.0',
    date: '2025-12-17',
    changes: [
      { type: 'new', text: '全新登录页和功能介绍页 UI 设计' },
      { type: 'impr', text: '采用 Fresh Academic 设计风格，更现代的视觉体验' },
      { type: 'impr', text: '毛玻璃效果 (Glassmorphism) 卡片设计' }
    ]
  },
  {
    version: 'v2.1.0',
    date: '2025-12-16',
    changes: [
      { type: 'new', text: '新增统一工作台 (Unified Workspace)：左文右图分屏模式' },
      { type: 'new', text: '新增拖拽交互：直接从 PDF 拖拽文本到画布生成卡片' },
      { type: 'new', text: '新增用户管理后台：支持封禁、解封用户' },
      { type: 'new', text: '新增邮箱验证码注册机制' },
      { type: 'impr', text: '移除旧版三步阅读法流程，交互更自由' },
      { type: 'fix', text: '优化了后端 API 的响应速度' }
    ]
  },
  {
    version: 'v2.0.0',
    date: '2025-10-27',
    changes: [
      { type: 'new', text: '引入无限画布 (Infinite Canvas) 系统' },
      { type: 'new', text: '集成 Keshav 三步阅读法理念' },
      { type: 'impr', text: '升级 AI 模型至 GLM-4.6' }
    ]
  },
  {
    version: 'v1.0.0',
    date: '2025-10-01',
    changes: [
      { type: 'new', text: '基础 PDF 阅读与标注功能' },
      { type: 'new', text: 'AI 侧边栏问答' }
    ]
  }
];

export default function FeatureIntro({ onStart, onBack }: FeatureIntroProps) {
  return (
    <div className="feature-intro-container">
      {/* 导航栏 */}
      <nav className="intro-nav">
        <div className="nav-logo" onClick={onBack}>PDF MindMap</div>
        <div className="nav-actions">
          <button className="cta-btn" onClick={onStart}>
            进入系统
          </button>
        </div>
      </nav>

      {/* Hero 区域 */}
      <header className="intro-hero">
        <div className="hero-content">
          <h1>
            科研阅读的 <span className="highlight">空间化</span> 革命
          </h1>
          <p className="subtitle">
            不仅仅是阅读器，更是你的第二大脑。<br/>
            将线性文档转化为可视化的知识网络，让深度思考自然发生。
          </p>
          <button className="cta-btn" onClick={onStart}>
            立即开始使用 →
          </button>
        </div>
      </header>

      {/* 核心功能 */}
      <section className="features-section">
        <h2 className="section-title">核心功能</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🖥️</div>
            <h3>Unified Workspace</h3>
            <p>
              左文右图的统一工作台布局。一边阅读文献，一边在画布上整理思路，
              告别窗口来回切换的烦恼，降低认知负荷。
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🤏</div>
            <h3>Drag & Drop</h3>
            <p>
              看到精彩内容？直接选中并拖拽到画布上。
              系统会自动生成包含原文引用的知识卡片，让摘录变得像拼积木一样简单。
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🤖</div>
            <h3>Context-Aware AI</h3>
            <p>
              AI 助手知道你正在读哪一页。提供完整的4阶段学习流程：
              快速浏览、深入理解、批判性总结、苏格拉底对话。
              支持文本和图片分析，引导你进行系统化的深度思考。
            </p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">♾️</div>
            <h3>Infinite Canvas</h3>
            <p>
              基于 Block 的无限画布。
              你可以自由排列卡片、建立连线、创建分组，
              将碎片化的知识点重组为结构化的知识图谱。
            </p>
          </div>
        </div>
      </section>

      {/* 更新日志 */}
      <section className="changelog-section">
        <div className="timeline">
          <h2 className="section-title">版本更新日志</h2>

          {CHANGELOG.map((log, index) => (
            <div key={index} className="version-item">
              <div className="version-dot"></div>
              <div className="version-header">
                <span className="version-number">{log.version}</span>
                <span className="version-date">{log.date}</span>
              </div>
              <div className="version-content">
                <ul>
                  {log.changes.map((change, i) => (
                    <li key={i}>
                      {change.type && (
                        <span className={`tag ${change.type}`}>
                          {change.type === 'new' ? 'NEW' : change.type === 'fix' ? 'FIX' : 'IMPR'}
                        </span>
                      )}
                      {change.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 页脚 */}
      <footer className="intro-footer">
        <p>&copy; {new Date().getFullYear()} PDF MindMap Intelligent Assistant. All rights reserved.</p>
      </footer>
    </div>
  );
}
